import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AttendeeLogCheckerModel } from '@/features/calendarView/store/AttendeeLogCheckerModel';
import { AttendeeLogCheckerPanel } from '@/features/calendarView/ui/AttendeeLogCheckerPanel';
import { getLogsUserId, useLogsMode } from '@/features/logsMode';
import { User } from '@/types/user';
import { YandexUser } from '@/types/yandex';

type UseInternalCompanionParams = {
  companionPanel: React.ReactNode | undefined;
  startDate: string;
  user: User | null;
  watchedTaskKey: string | undefined;
  watchedUsers: YandexUser[] | undefined;
  autoShowCompanion?: boolean;
};

type UseInternalCompanionResult = {
  showCompanion: boolean;
  toggleCompanion: () => void;
  panel: React.ReactNode;
};

export const useInternalCompanion = ({
  companionPanel,
  startDate,
  user,
  watchedTaskKey,
  watchedUsers,
  autoShowCompanion,
}: UseInternalCompanionParams): UseInternalCompanionResult => {
  const [showCompanion, setShowCompanion] = useState(!!autoShowCompanion);

  useEffect(() => {
    setShowCompanion(!!autoShowCompanion);
  }, [autoShowCompanion]);

  const toggleCompanion = useCallback(() => {
    setShowCompanion((prev) => !prev);
  }, []);

  const mode = useLogsMode();
  const internalModel = useMemo(() => new AttendeeLogCheckerModel(mode), [mode]);

  useEffect(() => {
    if (companionPanel) return;

    if (startDate) {
      internalModel.initWithDate(startDate);
    }

    if (user) {
      const myUserId = getLogsUserId({ user, mode });

      if (myUserId) {
        internalModel.setAvailableUsers([
          { uid: myUserId, display: `${user.fullName} (я)`, email: user.email },
        ]);
      }
    }
  }, [companionPanel, startDate, user, internalModel, mode]);

  useEffect(() => {
    if (companionPanel) return;

    internalModel.setTaskKey(watchedTaskKey || null);
  }, [companionPanel, watchedTaskKey, internalModel]);

  useEffect(() => {
    if (companionPanel) return;

    if (watchedUsers && watchedUsers.length > 0) {
      internalModel.setAvailableUsers(watchedUsers);
    }
  }, [companionPanel, watchedUsers, internalModel]);

  useEffect(() => {
    if (companionPanel) return;

    return () => internalModel.reset();
  }, [companionPanel, internalModel]);

  const panel = companionPanel ?? <AttendeeLogCheckerPanel model={internalModel} />;

  return {
    showCompanion,
    toggleCompanion,
    panel,
  };
};
